import type { Station } from '@/types';
import { formatDistance } from '@/utils/distance';
import { clsx } from 'clsx';

interface StationHeaderProps {
  station: Station;
  className?: string;
}

export function StationHeader({ station, className }: StationHeaderProps) {
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${station.latitude},${station.longitude}`;

  return (
    <div className={clsx('space-y-2', className)}>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{station.name}</h2>
          <p className="text-gray-600">{station.brand}</p>
        </div>
        
        {/* Distance badge */}
        <div className="flex-shrink-0 bg-brand-100 text-brand-700 px-3 py-1 rounded-full text-sm font-medium">
          {formatDistance(station.distance)}
        </div>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap gap-2">
        {station.isOpen ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Open now
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Closed
          </span>
        )}
        
        {station.temporaryClosure && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Temporarily closed
          </span>
        )}
        
        {station.isMotorway && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Motorway
          </span>
        )}
        
        {station.isSupermarket && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Supermarket
          </span>
        )}
      </div>

      {/* Directions link */}
      <a
        href={directionsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-brand-600 hover:text-brand-700 font-medium text-sm"
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Get directions
      </a>
    </div>
  );
}
